/**
 * AdditionalQueryViewElement — kreator formatek: klocek palety reprezentujący
 * widok dodatkowy / „Widok SQL" (`additional_query_view`, patrz
 * `RecursiveFormRenderer` PLACEHOLDER_TYPES).
 *
 * Mirror `AdditionalTableViewElement` (patrz nagłówek tamtego pliku dla pełnego
 * uzasadnienia „PO CO") — jedyna różnica to katalog (`ext_additional_views`
 * zamiast `ext_additional_tables`) i pole ustawień (`viewSlug` zamiast
 * `tableSlug`). Na REALNEJ stronie rekordu ten typ jest dispatchowany WYŁĄCZNIE
 * przez `contextRenderers['additional_query_view']` (widget `AdditionalQueryTab`,
 * patrz `document-additional-renderers.tsx` i lokalne mapy w
 * `ContractorDetail.tsx`/`EmployeeDetail.tsx`).
 *
 * `allowedModules` — zweryfikowane grepem: 7 klas dokumentów (przez
 * `buildDocumentAdditionalRenderers`) + `contractors` + `employees`. UWAGA:
 * BEZ `users` — `UserDetail.tsx` ma wpięte `additional_view`/`satellite_view`,
 * ale NIE `additional_query_view` (pre-existing rozjazd, niezależny od tej
 * zmiany — dodać `users` tutaj razem z wpięciem renderera na tamtej stronie).
 *
 * `render`/`propertiesEditor` są wołane jak ZWYKŁE FUNKCJE (nie komponenty) — żaden hook nie może
 * być wywołany BEZPOŚREDNIO w ich ciele; `propertiesEditor` montuje `AdditionalQueryViewSlugPicker`,
 * a `render` — `AdditionalQueryColumnsCanvas` jako komponenty-dzieci (hooki żyją TAM).
 *
 * UKŁAD KOLUMN (mirror tabeli dodatkowej): gdy kreator ma dostęp do danych tenanta
 * (`useBuilderEnvironment`), płótno pokazuje edytor kolumn wybranego widoku (`TableLayoutEditor`),
 * który zapisuje `settings.tableColumns` / `settings.hiddenColumnIds` tego umieszczenia widoku.
 * Kolumny widoku znane są dopiero po uruchomieniu zapytania (serwer zwraca je endpointem kolumn),
 * a gdy baza odrzuci zapytanie, płótno pokazuje jej powód. Bez dostępu (kreator osadzony w panelu
 * ITE, hosty bez providera) — dotychczasowy placeholder. Na układzie systemowym (`systemHideOnly`)
 * edytor działa w trybie `hideOnly` (tenant tylko ukrywa kolumny: `settings.hiddenColumnIds`), a
 * kreator zapisuje to w nakładce tenanta pod kluczem widoku (`additional_query:<viewSlug>`).
 * Zmiana widoku w panelu właściwości czyści układ kolumn poprzedniego widoku.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropdown } from '@/components/Dropdown';
import { useBuilderEnvironment } from '@/components/fields/builder-environment';
import type {
  LayoutElementDefinition,
  LayoutElementPropertiesEditorProps,
} from '@/components/fields/LayoutElementRegistry';
import { useAdditionalViewsList } from '@/lib/additional-views/api';
import { filterAdditionalTablesForModule } from '@/pages/system-modules/document-module-slugs';

import {
  AdditionalQueryColumnsCanvas,
  AdditionalQueryViewPlaceholder,
} from './AdditionalQueryColumnsCanvas';

/** Kartoteki, których strona detalu dispatchuje `additional_query_view` przez `contextRenderers`. */
const ADDITIONAL_QUERY_ALLOWED_MODULES = [
  'purchase_invoices',
  'sale_invoices',
  'contracts',
  'letters',
  'commercial',
  'internal',
  'specjalne',
  'contractors',
  'employees',
  'tasks',
];

/**
 * Bramka środowiska: wybór widoku czyta katalog widoków TENANTA, więc w kreatorze bez dostępu do
 * danych tenanta (osadzonym w panelu ITE, hosty bez providera) pokazujemy tylko krótką
 * informację i NIE montujemy komponentu, który odpytuje API (jego hooki żyją w
 * `AdditionalQueryViewSlugPickerLoaded`) — mirror `AdditionalTableSlugPicker`.
 */
function AdditionalQueryViewSlugPicker(props: LayoutElementPropertiesEditorProps) {
  const { t } = useTranslation();
  const { tenantData } = useBuilderEnvironment();

  if (!tenantData) {
    return (
      <p
        className="text-sm text-text-tertiary"
        data-testid="additional-query-view-picker-unavailable"
      >
        {t('additionalViews.tableSelector.unavailableHere')}
      </p>
    );
  }
  return <AdditionalQueryViewSlugPickerLoaded {...props} />;
}

function AdditionalQueryViewSlugPickerLoaded({
  settings,
  onChange,
  moduleSlug,
}: LayoutElementPropertiesEditorProps) {
  const { t } = useTranslation();
  const viewsQuery = useAdditionalViewsList();
  const filtered = filterAdditionalTablesForModule(viewsQuery.data ?? [], moduleSlug ?? '');
  const value = typeof settings.viewSlug === 'string' ? settings.viewSlug : '';

  // Błąd pobrania katalogu (403 brak uprawnienia / 500 / sieć) NIE jest tym samym co
  // "brak widoków dla modułu" — mirror `AdditionalTableSlugPicker`.
  useEffect(() => {
    if (viewsQuery.isError) {
      console.warn(
        '[additional_query_view] Nie udało się pobrać katalogu widoków dodatkowych',
        viewsQuery.error,
      );
    }
  }, [viewsQuery.isError, viewsQuery.error]);

  return (
    <div className="space-y-2">
      <span className="block text-xs text-text-secondary">
        {t('additionalViews.tableSelector.hint')}
      </span>
      {viewsQuery.isPending ? (
        <p className="text-sm text-text-muted">{t('common.loading')}</p>
      ) : viewsQuery.isError ? (
        <p className="text-sm text-danger-600">{t('additionalViews.tableSelector.loadError')}</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-text-tertiary">
          {t('additionalViews.tableSelector.noViewsForModule')}
        </p>
      ) : (
        <Dropdown
          value={value}
          // Zmiana widoku czyści układ kolumn poprzedniego widoku (id kolumn to nazwy kolumn jego
          // zapytania) — inaczej zostałby martwy zapis, a po powrocie do tamtego widoku układ
          // wróciłby niepytany.
          onChange={(v) =>
            onChange({ viewSlug: v, tableColumns: undefined, hiddenColumnIds: undefined })
          }
          data-testid="additional-query-view-picker-selector"
          options={[
            { value: '', label: t('additionalViews.tableSelector.placeholder') },
            ...filtered.map((v) => ({ value: v.slug, label: v.displayName })),
          ]}
        />
      )}
    </div>
  );
}

export const AdditionalQueryViewElement: LayoutElementDefinition = {
  type: 'additional_query_view',
  category: 'tresc',
  isContainer: false,
  label: 'Widok SQL',
  icon: 'Database',
  allowedModules: ADDITIONAL_QUERY_ALLOWED_MODULES,
  // Edytor kolumn na płótnie jest szeroką tabelą — cały wiersz gridu sekcji, nie pół komórki.
  fullWidth: true,
  defaultSettings: () => ({}),
  propertiesEditor: (props) => <AdditionalQueryViewSlugPicker {...props} />,
  // Płótno kreatora = edytor układu kolumn (albo placeholder bez dostępu do danych tenanta);
  // poza kreatorem zostaje sam placeholder — realną stronę rekordu obsługuje `contextRenderers`.
  // Komponenty-dzieci niosą hooki (ta funkcja jest wołana jak zwykła, bez hooków).
  render: ({ element, mode, onSettingsChange, systemHideOnly }) =>
    mode === 'builder' ? (
      <AdditionalQueryColumnsCanvas
        element={element}
        onSettingsChange={onSettingsChange}
        systemHideOnly={systemHideOnly === true}
      />
    ) : (
      <AdditionalQueryViewPlaceholder elementId={element.id} />
    ),
};
